/**
 * Authentication initialization.
 */

import { tokenService, tokensFromRefresh } from '../services/tokenService'
import { httpService } from '../services/httpService'
import { permissionsService, type PermissionsData } from '../services/permissionsService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'

const PUBLIC_ROUTES = [
  '/login',
  '/register',
  '/confirm',
  '/forgot-password',
  '/reset-password',
  '/politica-de-privacidade',
  '/termos-de-servico',
  '/data-deletion',
  '/preencher-cadastro',
  '/laudo'
]

const isPublicRoute = (): boolean => {
  const currentPath = window.location.pathname
  return PUBLIC_ROUTES.some((route) => currentPath.startsWith(route))
}

const refreshTokens = async (): Promise<void> => {
  log('App', 'bootstrap', '🔄 Refreshing tokens from the httpOnly cookie...')

  const refreshResponse = await httpService.post<unknown>(config.api.endpoints.cognito.refreshToken)
  log('App', 'bootstrap', '🔍 Refresh response:', refreshResponse)

  const tokens = tokensFromRefresh(refreshResponse)
  if (tokens) {
    tokenService.setTokens(tokens.accessToken, tokens.idToken)
    log('App', 'bootstrap', '✅ Tokens refreshed successfully from cookie')
    return
  }

  log('App', 'bootstrap', '⚠️ Token refresh failed - unexpected response format:', refreshResponse)
  throw new Error('No valid refresh token cookie')
}

const redirectToLogin = (): void => {
  const redirectPath = window.location.pathname + window.location.search
  log('App', 'bootstrap', `🔀 Redirecting to Vue login: /login?redirectTo=${redirectPath}`)
  window.location.href = `/login?redirectTo=${encodeURIComponent(redirectPath)}`
}

export async function initAuth(): Promise<PermissionsData | null | undefined> {
  log('App', 'bootstrap', 'Fetching permissions...')

  if (isPublicRoute()) {
    log('App', 'bootstrap', '🔓 Public auth route - skipping permissions check')
    return null
  }

  try {
    if (!tokenService.hasTokens()) {
      await refreshTokens()
    } else {
      log('App', 'bootstrap', '✅ Tokens already in memory (from login)')
    }
    // After a reload the tokens come from the cookie and nobody else starts the
    // scheduler, so the 1h access token used to expire mid-session (DS 2026-0070).
    tokenService.startRefreshScheduler(refreshTokens)

    const permissions = await permissionsService.initialize({
      minutesToRefresh: 10,
      autoRefresh: true
    })
    log('App', 'bootstrap', '✅ Permissions loaded', permissions)

    return permissions
  } catch (authError) {
    log('App', 'bootstrap', '❌ Auth failed, redirecting to login', authError)
    redirectToLogin()
    return undefined
  }
}
